// ── Accent color helpers ────────────────────────────────────────────────────
// Extracted so TripperWidget can apply the itinerary's primaryColor
// as a fallback via direct DOM mutation (no React state change).

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  const v = parseInt(m[1], 16);
  return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
}

/**
 * Ensures a hex color string has a leading `#`. Itinerary settings sometimes
 * store colors without it (e.g. "5696CD"), which CSS tolerates but strict
 * consumers like Mapbox GL reject. Returns `fallback` for non-hex input.
 */
export function normalizeHexColor(
  color: string | null | undefined,
  fallback: string,
): string {
  if (!color) return fallback;
  const trimmed = color.trim();
  if (/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed)) {
    return trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  }
  return fallback;
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    "#" +
    [r, g, b]
      .map((v) => Math.round(v).toString(16).padStart(2, "0"))
      .join("")
  );
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b),
    d = max - min;
  let h = 0,
    s = 0;
  const l = (max + min) / 2;
  if (d > 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return [h * 360, s * 100, l * 100];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h /= 360;
  s /= 100;
  l /= 100;
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s,
    p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255),
  ];
}

function luminance(r: number, g: number, b: number): number {
  const toLinear = (c: number) => {
    c /= 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

function contrastRatio(l1: number, l2: number): number {
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

function enforceAAContrast(hexColor: string): string {
  const rgb = hexToRgb(hexColor);
  if (!rgb) return hexColor;
  let [h, s, l] = rgbToHsl(...rgb);
  const surfaceLum = 1;
  while (l > 5) {
    const [tr, tg, tb] = hslToRgb(h, s, l);
    if (contrastRatio(surfaceLum, luminance(tr, tg, tb)) >= 4.5) break;
    l -= 1;
  }
  const [fr, fg, fb] = hslToRgb(h, s, l);
  return rgbToHex(fr, fg, fb);
}

export function deriveAccentColors(inputHex: string) {
  const rgb = hexToRgb(inputHex);
  if (!rgb) return null;

  const bold = enforceAAContrast(inputHex);
  const [br, bg_, bb] = hexToRgb(bold)!;
  const [h, s, l] = rgbToHsl(br, bg_, bb);

  const textRgb = hslToRgb(h, Math.min(s + 5, 100), Math.max(l - 8, 0));
  const text = rgbToHex(...textRgb);

  const bgRgb = hslToRgb(h, Math.min(s * 0.35, 30), 91);
  const bg = rgbToHex(...bgRgb);

  const borderRgb = hslToRgb(h, Math.min(s * 0.55, 50), 72);
  const border = rgbToHex(...borderRgb);

  return { bold, text, bg, border };
}

/**
 * Apply accent color CSS custom properties directly to a DOM element.
 * Used as a fallback when no explicit widget-level override was provided
 * — the itinerary's primaryColor is written via DOM mutation so it doesn't
 * trigger a React re-render in the provider tree.
 */
export function applyAccentColorToDiv(div: HTMLElement, color: string): void {
  const accent = deriveAccentColors(color);
  if (!accent) return;

  div.style.setProperty("--tripper-primary", color);
  div.style.setProperty("--tripper-marker-color", color);
  div.style.setProperty("--tripper-route-line-color", color);
  div.style.setProperty("--tripper-place-fill-color", color);
  div.style.setProperty("--tripper-accent-bold", accent.bold);
  div.style.setProperty("--tripper-accent-text", accent.text);
  div.style.setProperty("--tripper-accent-bg", accent.bg);
  div.style.setProperty("--tripper-accent-border", accent.border);
}
